import type { JsonSchemaType } from '@gmn-portal/backend';
import { DateTime } from 'luxon';
import { useCustomer, useObjects } from '@/core/api';
import { useGlobalMetadata } from '@/core/api/global-metadata';
import { useIntl } from '@/core/i18n/use-intl';
import { formatCurrencyFromCents } from '@/shared/utils/money';

import type { ResolvedFieldValueProps } from './types';

export function ResolvedFieldValue({
  field,
  value,
  contract,
  metadataSchemaOverride,
  dynamicSchemaOverride,
}: ResolvedFieldValueProps) {
  const { t, locale } = useIntl();
  const { data: globalMetadata } = useGlobalMetadata();

  if (value === null || value === undefined || value === '') {
    return <span>{t('activity.none')}</span>;
  }

  if (field === 'dynamicMetadataSchema') {
    if (typeof value !== 'object' || !value) {
      return <span>{t('activity.none')}</span>;
    }
    const schema = value as JsonSchemaType;
    const count = Object.keys(schema.properties || {}).length;
    return <span>{t('activity.schemaSummary', { count })}</span>;
  }

  // Handle status field specifically
  if (field === 'status') {
    if (value === 'open' || value === 'closed') {
      return <span>{t(`contracts.status.${value}`)}</span>;
    }
  }

  if (field === 'billingInterval' && typeof value === 'string') {
    return <span>{t(`contracts.billingIntervalOptions.${value}` as any)}</span>;
  }

  if (field === 'billingGenerationMode' && typeof value === 'string') {
    return <span>{t(`contracts.billingGenerationMode.${value}` as any)}</span>;
  }

  if (field === 'billingTotalCents' && typeof value === 'number') {
    return <span>{formatCurrencyFromCents(value, locale)}</span>;
  }

  if (field === 'paymentTermsDays' && typeof value === 'number') {
    return <span>{value}</span>;
  }

  // Handle metadata fields that may be nested objects/relationships
  if (field.startsWith('metadata.')) {
    const metadataKey = field.split('.')[1];

    const metadataSchema =
      metadataSchemaOverride ??
      (globalMetadata?.rootContractMetadata as JsonSchemaType | undefined);

    // Check if this field has a specific type in the schema
    const fieldSchema = metadataSchema?.properties?.[metadataKey];

    if (fieldSchema?.type === 'date' && typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return <span>{date.toFormat('dd.MM.yyyy')}</span>;
      }
      return <span>{String(value)}</span>;
    }
  }

  // Handle dynamic metadata fields
  if (field.startsWith('dynamicMetadata.')) {
    const metadataKey = field.split('.')[1];
    const fieldSchema =
      dynamicSchemaOverride?.properties?.[metadataKey] ??
      contract?.dynamicMetadataSchema?.properties?.[metadataKey];

    if (fieldSchema?.type === 'date' && typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return <span>{date.toFormat('dd.MM.yyyy')}</span>;
      }
      return <span>{String(value)}</span>;
    }
  }

  // Handle objects field
  if (field === 'objects' && Array.isArray(value)) {
    return <ObjectsDisplay objectIds={value} />;
  }

  if (field === 'aggregatedObjects' && Array.isArray(value)) {
    return <ObjectsDisplay objectIds={value} />;
  }

  // Handle customer field
  if (field === 'customer' && typeof value === 'string') {
    return <CustomerDisplay customerId={value} />;
  }

  if (field.startsWith('billingPeriods.')) {
    if (typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return <span>{date.toFormat('dd.MM.yyyy')}</span>;
      }
      return <span>{String(value)}</span>;
    }

    if (typeof value === 'number') {
      if (field.endsWith('amountCents')) {
        return <span>{formatCurrencyFromCents(value, locale)}</span>;
      }
      return <span>{value}</span>;
    }

    if (typeof value === 'object' && value) {
      const v = value as Record<string, unknown>;
      if (typeof v.startDate === 'string' && typeof v.endDate === 'string') {
        const start = DateTime.fromISO(v.startDate);
        const end = DateTime.fromISO(v.endDate);
        if (start.isValid && end.isValid) {
          return <span>{`${start.toFormat('dd.MM.yyyy')} – ${end.toFormat('dd.MM.yyyy')}`}</span>;
        }
      }
      return <span>{t('activity.none')}</span>;
    }
  }

  if (field.endsWith('.status') && typeof value === 'string') {
    return <span>{t(`contracts.billingStatus.${value}` as any)}</span>;
  }

  // Handle other fields
  if (typeof value === 'string') {
    return <span>{value}</span>;
  }

  if (typeof value === 'number') {
    return <span>{value}</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? t('common.yes') : t('common.no')}</span>;
  }

  // Fallback for other types
  return <span>{String(value)}</span>;
}

const ObjectsDisplay = ({ objectIds }: { objectIds: string[] }) => {
  const { data: objectsData, isLoading } = useObjects({
    where: {
      id: {
        in: objectIds,
      },
    },
    limit: objectIds.length,
    disabled: !objectIds || objectIds.length === 0,
  });

  if (isLoading) return <span>Loading...</span>;

  const objects = objectsData?.docs || [];
  if (objects.length === 0) return <span>-</span>;

  return <span>{objects.map((obj) => obj.name).join(', ')}</span>;
};

const CustomerDisplay = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);

  if (isLoading) return <span>Loading...</span>;
  return <span>{customer?.name || '-'}</span>;
};
